// Resort-specific FAQs — Real Estate only.
//
// ★ 2026-08-13 (Fase 51 · Compliance CONSOB) — RISCRITTURA COMPLETA
// Rimossi tutti i riferimenti a "Equity Share", "quote SPV", "diventa socio",
// "primi 20 soci", "benefit a vita", prezzi quote (€40k/€70k/€30k).
// Il modello pubblico è UNICAMENTE l'acquisto immobiliare (Real Estate).
//
// ★ 2026-08-14 (Fase 55 · Multi-lingua) — Ogni q/a è ora un oggetto
// { it, en, es }. Il componente `ResortFaq` seleziona la lingua corrente
// via `pickText(field, lang)` con fallback su italiano.

/**
 * Restituisce il testo per la lingua corrente, con fallback IT.
 * Supporta sia stringhe (legacy) sia oggetti multi-lingua.
 */
export function pickText(field, lang = 'it') {
  if (field == null) return '';
  if (typeof field === 'string') return field;
  return field[lang] || field.it || field.en || Object.values(field)[0] || '';
}

// ── Data: 4 resort × ~8-10 FAQ ─────────────────────────────────────────
export const RESORT_FAQS = {
  lombok: [
    {
      q: {
        it: 'Perché Lombok? Cosa la rende strategica?',
        en: 'Why Lombok? What makes it strategic?',
        es: '¿Por qué Lombok? ¿Qué la hace estratégica?',
      },
      a: {
        it: 'Lombok è dichiarata Special Economic Zone (Mandalika SEZ) dal governo indonesiano. Ospita il MotoGP dal 2021 e il turismo è in crescita. Aeroporto internazionale a 30 min, voli diretti su Singapore, Sydney, Kuala Lumpur. Bali è satura, Lombok rappresenta una nuova destinazione del lusso asiatico a prezzi ancora competitivi.',
        en: 'Lombok has been designated a Special Economic Zone (Mandalika SEZ) by the Indonesian government. It has hosted MotoGP since 2021 and tourism is growing rapidly. International airport 30 minutes away with direct flights to Singapore, Sydney, and Kuala Lumpur. While Bali is saturated, Lombok represents a new Asian luxury destination at still-competitive prices.',
        es: 'Lombok ha sido declarada Zona Económica Especial (Mandalika SEZ) por el gobierno indonesio. Alberga el MotoGP desde 2021 y el turismo está en crecimiento. Aeropuerto internacional a 30 min, vuelos directos a Singapur, Sídney y Kuala Lumpur. Bali está saturada, Lombok representa un nuevo destino del lujo asiático con precios aún competitivos.',
      },
    },
    {
      q: {
        it: 'Quanto costa un appartamento a Green Paradise Lombok?',
        en: 'How much does an apartment at Green Paradise Lombok cost?',
        es: '¿Cuánto cuesta un apartamento en Green Paradise Lombok?',
      },
      a: {
        it: 'Il listino ufficiale va da €169.100 (bilocale 89 mq, piano 1) a €220.500 (bilocale premium 105 mq, piano rooftop). Il piano pagamenti è rateale: caparra + anticipo 30% alla firma + 12 rate mensili + saldo 20% alla consegna. Nessun mutuo, nessuna banca: paghi direttamente al developer con contratto notarile locale.',
        en: 'The official price list ranges from €169,100 (89 sqm 2-room, 1st floor) to €220,500 (105 sqm premium rooftop). The payment plan is instalment-based: deposit + 30% down payment on signature + 12 monthly instalments + 20% balance at delivery. No mortgage, no bank required — you pay the developer directly with a local notarial contract.',
        es: 'La lista oficial va desde €169.100 (2 ambientes 89 m², planta 1) hasta €220.500 (2 ambientes premium 105 m², planta rooftop). El plan de pagos es en cuotas: depósito + anticipo del 30% a la firma + 12 cuotas mensuales + saldo del 20% en la entrega. Sin hipoteca ni banco: pagas directamente al promotor con contrato notarial local.',
      },
    },
    {
      q: {
        it: 'Quanti appartamenti sono ancora disponibili?',
        en: 'How many apartments are still available?',
        es: '¿Cuántos apartamentos siguen disponibles?',
      },
      a: {
        it: '140 unità totali su 14 blocchi (10 unità per blocco). Attualmente tutti gli appartamenti sono disponibili nella fase pre-launch. La tabella pubblica live mostra codice, piano, taglio (89 o 105 mq) e prezzo per ogni singola unità e viene aggiornata in tempo reale al momento della vendita.',
        en: '140 units total across 14 blocks (10 units per block). Currently all apartments are available in the pre-launch phase. The live public table shows the code, floor, size (89 or 105 sqm), and price for each unit and is updated in real time as sales occur.',
        es: '140 unidades totales en 14 bloques (10 unidades por bloque). Actualmente todos los apartamentos están disponibles en fase pre-launch. La tabla pública en vivo muestra código, planta, superficie (89 o 105 m²) y precio de cada unidad, y se actualiza en tiempo real al momento de la venta.',
      },
    },
    {
      q: {
        it: 'Come funziona la proprietà in Indonesia per gli stranieri?',
        en: 'How does property ownership in Indonesia work for foreigners?',
        es: '¿Cómo funciona la propiedad en Indonesia para extranjeros?',
      },
      a: {
        it: 'Il modello legale è Hak Pakai (Right to Use) o Leasehold a lungo termine, struttura standard e solida per acquirenti stranieri. Il team Legal HCF si occupa di tutta la documentazione notarile in Indonesia, inclusi KYC e la registrazione presso la SPV indonesiana dedicata al resort.',
        en: 'The legal framework is Hak Pakai (Right to Use) or long-term Leasehold — a standard, robust structure for foreign buyers. The HCF Legal team handles all notarial documentation in Indonesia, including KYC and registration with the Indonesian SPV dedicated to the resort.',
        es: 'El marco legal es Hak Pakai (Derecho de Uso) o Leasehold a largo plazo, una estructura estándar y sólida para compradores extranjeros. El equipo Legal HCF se ocupa de toda la documentación notarial en Indonesia, incluyendo KYC y la inscripción en la SPV indonesia dedicada al resort.',
      },
    },
    {
      q: {
        it: 'Quando apre il resort?',
        en: 'When will the resort open?',
        es: '¿Cuándo abre el resort?',
      },
      a: {
        it: 'Grand opening previsto Q4 2027-2028. Il piano pagamenti a 12 rate copre l\u2019intero periodo di costruzione. Tu paghi gradualmente, il resort si costruisce in parallelo.',
        en: 'Grand opening scheduled for Q4 2027-2028. The 12-instalment payment plan covers the entire construction period. You pay gradually while the resort is built in parallel.',
        es: 'Grand opening previsto para Q4 2027-2028. El plan de pagos en 12 cuotas cubre todo el período de construcción. Pagas gradualmente mientras el resort se construye en paralelo.',
      },
    },
    {
      q: {
        it: 'Come avviene la gestione operativa del resort?',
        en: 'How is the resort operated day-to-day?',
        es: '¿Cómo funciona la gestión operativa del resort?',
      },
      a: {
        it: 'Il resort sarà gestito da un operatore hospitality professionale con know-how internazionale. Alla consegna potrai scegliere: usarlo personalmente per le tue vacanze, oppure metterlo a reddito tramite il property management Green Paradise (rendimento locativo variabile in funzione di stagionalità, occupancy e mercato). Non ci sono promesse di rendimento garantito.',
        en: 'The resort will be run by a professional hospitality operator with international expertise. At delivery you can choose: use it personally for your holidays, or generate income through Green Paradise property management (rental yield varies with seasonality, occupancy, and market). There are no guaranteed return promises.',
        es: 'El resort será gestionado por un operador hotelero profesional con know-how internacional. En la entrega podrás elegir: usarlo personalmente para tus vacaciones, o ponerlo en renta a través del property management Green Paradise (rendimiento locativo variable según estacionalidad, ocupación y mercado). No hay promesas de rentabilidad garantizada.',
      },
    },
    {
      q: {
        it: 'Posso visitare il sito a Lombok?',
        en: 'Can I visit the site in Lombok?',
        es: '¿Puedo visitar el sitio en Lombok?',
      },
      a: {
        it: 'Sì, organizziamo Verification Pass on-site per acquirenti qualificati. Visita guidata al cantiere, incontro con architetti e team locale, due giorni in resort partner per provare l\u2019experience. Costi del viaggio scontati per chi conclude la qualifica.',
        en: 'Yes, we organise on-site Verification Passes for qualified buyers. Guided tour of the construction site, meeting with architects and the local team, plus two days at a partner resort to experience the offering first-hand. Travel costs are discounted for those who complete the qualification.',
        es: 'Sí, organizamos Verification Pass on-site para compradores calificados. Visita guiada a la obra, encuentro con arquitectos y equipo local, dos días en resort partner para probar la experiencia. Costos de viaje descontados para quienes completan la calificación.',
      },
    },
    {
      q: {
        it: 'Posso rivendere l\u2019appartamento in futuro?',
        en: 'Can I resell the apartment in the future?',
        es: '¿Puedo revender el apartamento en el futuro?',
      },
      a: {
        it: 'Sì. Alla consegna sei proprietario dell\u2019immobile (freehold nel modello Leasehold indonesiano) e puoi rivendere sul mercato secondario. Il marketplace interno HCF Estate (hcf.estate) facilita l\u2019incontro fra proprietari e nuovi acquirenti, con procedura KYC e supporto notarile.',
        en: 'Yes. At delivery you become the property owner (freehold under the Indonesian Leasehold model) and you can resell on the secondary market. The internal HCF Estate marketplace (hcf.estate) connects sellers with new buyers, with KYC procedure and notarial support.',
        es: 'Sí. En la entrega eres propietario del inmueble (freehold en el modelo Leasehold indonesio) y puedes revender en el mercado secundario. El marketplace interno HCF Estate (hcf.estate) facilita el encuentro entre propietarios y nuevos compradores, con procedimiento KYC y soporte notarial.',
      },
    },
    {
      q: {
        it: 'Cosa succede in caso di disastri naturali o instabilità?',
        en: 'What happens in case of natural disasters or instability?',
        es: '¿Qué pasa en caso de desastres naturales o inestabilidad?',
      },
      a: {
        it: 'Il resort è progettato secondo standard antisismici indonesiani avanzati. La SPV ha polizza assicurativa multirischio (terremoto, tsunami, business interruption). Lombok è considerata zona turistica stabile dai principali Foreign Office UE. La diversificazione su più paesi (Albania, Indonesia, Thailandia) mitiga ulteriormente il rischio geografico, senza eliminarlo.',
        en: 'The resort is designed to advanced Indonesian seismic standards. The SPV carries multi-risk insurance (earthquake, tsunami, business interruption). Lombok is considered a stable tourist area by leading EU Foreign Offices. Diversification across multiple countries (Albania, Indonesia, Thailand) further mitigates — but does not eliminate — geographic risk.',
        es: 'El resort está diseñado según estándares antisísmicos indonesios avanzados. La SPV cuenta con póliza de seguros multirriesgo (terremoto, tsunami, interrupción de negocio). Lombok es considerada zona turística estable por las principales cancillerías de la UE. La diversificación en varios países (Albania, Indonesia, Tailandia) mitiga aún más el riesgo geográfico, sin eliminarlo.',
      },
    },
    {
      q: {
        it: 'Posso vedere i documenti ufficiali della società?',
        en: 'Can I see the official company documents?',
        es: '¿Puedo ver los documentos oficiales de la sociedad?',
      },
      a: {
        it: 'Sì. La documentazione societaria completa (atto costitutivo PT GPS, registrazioni ministeriali, statuto della SPV) viene condivisa dopo una call informativa con il team senior HCF Property. Prenota una consulenza per ricevere tutta la documentazione via email.',
        en: 'Yes. The full corporate documentation (PT GPS incorporation deed, ministerial registrations, SPV bylaws) is shared after an informational call with the senior HCF Property team. Book a consultation to receive the complete documentation by email.',
        es: 'Sí. La documentación societaria completa (acta constitutiva PT GPS, registros ministeriales, estatuto de la SPV) se comparte tras una llamada informativa con el equipo senior HCF Property. Reserva una consulta para recibir toda la documentación por email.',
      },
    },
  ],

  spille: [
    {
      q: {
        it: 'Perché Spille (Albania)? Cosa la rende speciale?',
        en: 'Why Spille (Albania)? What makes it special?',
        es: '¿Por qué Spille (Albania)? ¿Qué la hace especial?',
      },
      a: {
        it: 'Spille è una località della Costa Albanese in fase di sviluppo turistico strutturato. Ha spiaggia sabbiosa, mare cristallino, aeroporto internazionale di Tirana a 60 min. L\u2019Albania è candidata all\u2019Unione Europea con proprietà freehold diretta anche per stranieri e fiscalità immobiliare vantaggiosa. Green Paradise Spille è il progetto premium della zona.',
        en: 'Spille is a location on the Albanian coast undergoing structured tourism development. Sandy beach, crystal-clear sea, Tirana international airport 60 minutes away. Albania is a candidate for the European Union, offering direct freehold ownership to foreigners and favourable real-estate taxation. Green Paradise Spille is the premium project of the area.',
        es: 'Spille es una localidad de la Costa Albanesa en fase de desarrollo turístico estructurado. Playa de arena, mar cristalino, aeropuerto internacional de Tirana a 60 min. Albania es candidata a la Unión Europea, con propiedad freehold directa incluso para extranjeros y fiscalidad inmobiliaria ventajosa. Green Paradise Spille es el proyecto premium de la zona.',
      },
    },
    {
      q: {
        it: 'Ci sono ancora appartamenti disponibili a Spille?',
        en: 'Are there still apartments available in Spille?',
        es: '¿Todavía hay apartamentos disponibles en Spille?',
      },
      a: {
        it: 'Al momento il Real Estate di Green Paradise Spille risulta esaurito: tutte e 80 le unità sono già state assegnate. Stiamo lavorando a nuove disponibilità che verranno comunicate ufficialmente. Se sei interessato, iscriviti alla waiting list o valuta gli altri resort del network (Lombok, Chumphon, La Mar Residence, Narea).',
        en: 'At present Green Paradise Spille is sold out: all 80 units have been assigned. New availability is being planned and will be officially announced. If you\u2019re interested, join the waiting list or explore the other resorts in the network (Lombok, Chumphon, La Mar Residence, Narea).',
        es: 'Por el momento Green Paradise Spille está agotado: las 80 unidades ya han sido asignadas. Estamos trabajando en nuevas disponibilidades que se comunicarán oficialmente. Si te interesa, inscríbete en la lista de espera o valora los otros resorts de la red (Lombok, Chumphon, La Mar Residence, Narea).',
      },
    },
    {
      q: {
        it: 'Come funziona la proprietà in Albania?',
        en: 'How does property ownership in Albania work?',
        es: '¿Cómo funciona la propiedad en Albania?',
      },
      a: {
        it: 'Freehold diretto: sei proprietario a tutti gli effetti, esattamente come in Italia, con atto notarile locale e registrazione al catasto albanese. Il team Legal HCF gestisce tutta la documentazione. Fiscalità immobiliare vantaggiosa, tassazione locativa contenuta.',
        en: 'Direct freehold: you are the full legal owner, exactly as in Italy, with local notarial deed and registration in the Albanian land registry. The HCF Legal team handles all documentation. Favourable property taxation and low rental tax rates.',
        es: 'Freehold directo: eres propietario a todos los efectos, exactamente como en Italia, con escritura notarial local e inscripción en el catastro albanés. El equipo Legal HCF gestiona toda la documentación. Fiscalidad inmobiliaria ventajosa, tributación locativa reducida.',
      },
    },
    {
      q: {
        it: 'Quando apre il resort Spille?',
        en: 'When will the Spille resort open?',
        es: '¿Cuándo abre el resort Spille?',
      },
      a: {
        it: 'Grand opening previsto Q4 2027. Il cantiere è in fase avanzata di sviluppo e i cronoprogrammi vengono aggiornati periodicamente. Chi ha già acquistato riceve report mensili sull\u2019avanzamento.',
        en: 'Grand opening scheduled for Q4 2027. The construction site is in an advanced development phase and schedules are updated periodically. Existing buyers receive monthly progress reports.',
        es: 'Grand opening previsto para Q4 2027. La obra está en fase avanzada de desarrollo y los cronogramas se actualizan periódicamente. Quienes ya han comprado reciben reportes mensuales del avance.',
      },
    },
    {
      q: {
        it: 'Posso visitare il cantiere Spille?',
        en: 'Can I visit the Spille construction site?',
        es: '¿Puedo visitar la obra de Spille?',
      },
      a: {
        it: 'Sì, i Verification Pass a Spille sono organizzati con cadenza regolare. Ti portiamo direttamente sul cantiere, incontri il site manager, verifichi con i tuoi occhi lo stato lavori. Costo viaggio scontato per acquirenti in fase di due diligence.',
        en: 'Yes, Verification Passes to Spille are organised on a regular schedule. We take you directly to the site, you meet the site manager and verify progress with your own eyes. Discounted travel costs for buyers in due diligence.',
        es: 'Sí, los Verification Pass en Spille se organizan con cadencia regular. Te llevamos directamente a la obra, conoces al site manager y verificas con tus propios ojos el estado de los trabajos. Costo de viaje descontado para compradores en fase de due diligence.',
      },
    },
    {
      q: {
        it: 'Come funziona la gestione locativa?',
        en: 'How does rental management work?',
        es: '¿Cómo funciona la gestión locativa?',
      },
      a: {
        it: 'Il property management Green Paradise si occupa di tutto: canale bookings, pulizie, manutenzione, adempimenti fiscali locali. Il rendimento locativo è variabile e dipende da stagionalità e mercato: nessuna promessa di rendimento garantito.',
        en: 'Green Paradise property management handles everything: booking channel, cleaning, maintenance, local tax compliance. Rental yield is variable and depends on seasonality and the market — no guaranteed return promises.',
        es: 'El property management de Green Paradise se encarga de todo: canal de reservas, limpieza, mantenimiento, cumplimiento fiscal local. El rendimiento locativo es variable y depende de la estacionalidad y del mercado: no hay promesas de rentabilidad garantizada.',
      },
    },
    {
      q: {
        it: 'Cosa succede se voglio uscire prima della consegna?',
        en: 'What happens if I want to exit before delivery?',
        es: '¿Qué pasa si quiero salir antes de la entrega?',
      },
      a: {
        it: 'Prima della firma preliminare non c\u2019è alcun impegno vincolante. Dopo la firma si applicano le clausole di recesso previste dal contratto notarile locale (variabili per fase). Alla consegna sei proprietario freehold e puoi rivendere sul mercato secondario o tramite il marketplace HCF Estate.',
        en: 'Before the preliminary signing there is no binding commitment. After signing, the withdrawal clauses in the local notarial contract apply (vary by phase). At delivery you become a freehold owner and can resell on the secondary market or through the HCF Estate marketplace.',
        es: 'Antes de la firma preliminar no hay ningún compromiso vinculante. Tras la firma se aplican las cláusulas de desistimiento previstas por el contrato notarial local (variables por fase). En la entrega eres propietario freehold y puedes revender en el mercado secundario o a través del marketplace HCF Estate.',
      },
    },
    {
      q: {
        it: 'Green Paradise Spille o La Mar Residence — quale scegliere?',
        en: 'Green Paradise Spille or La Mar Residence — which to choose?',
        es: 'Green Paradise Spille o La Mar Residence — ¿cuál elegir?',
      },
      a: {
        it: 'Sono progetti diversi con posizionamento diverso. Green Paradise Spille è un resort premium con appartamenti dentro il complesso alberghiero (property management, servizi hospitality, dimensione turistica). La Mar Residence è un condominio residenziale fronte mare a Golem (Albania) con bilocali dalle finiture premium e piano pagamenti 5 rate. Parla con un Senior Associate per capire quale fa per te.',
        en: 'They are different projects with different positioning. Green Paradise Spille is a premium resort with apartments inside the hotel complex (property management, hospitality services, tourism-oriented). La Mar Residence is a residential seafront condominium in Golem (Albania) with premium-finish 2-room units and a 5-instalment payment plan. Talk to a Senior Associate to figure out which one suits you.',
        es: 'Son proyectos distintos con posicionamiento diferente. Green Paradise Spille es un resort premium con apartamentos dentro del complejo hotelero (property management, servicios de hospitalidad, dimensión turística). La Mar Residence es un condominio residencial frente al mar en Golem (Albania) con dos ambientes de acabados premium y plan de pagos en 5 cuotas. Habla con un Senior Associate para saber cuál te conviene.',
      },
    },
  ],

  chumphon: [
    {
      q: {
        it: 'Perché Chumphon (Thailandia)?',
        en: 'Why Chumphon (Thailand)?',
        es: '¿Por qué Chumphon (Tailandia)?',
      },
      a: {
        it: 'Chumphon è la porta d\u2019accesso al sud della Thailandia, con aeroporto internazionale, stazione di partenza per Koh Tao e Koh Phangan. Turismo in forte crescita, prezzi ancora competitivi rispetto a Phuket o Koh Samui. La Thailandia offre un contesto legale stabile per acquirenti stranieri con società locale.',
        en: 'Chumphon is the gateway to southern Thailand, with an international airport and the departure hub for Koh Tao and Koh Phangan. Tourism is growing fast, and prices remain competitive compared with Phuket or Koh Samui. Thailand offers a stable legal framework for foreign buyers through a local company.',
        es: 'Chumphon es la puerta de acceso al sur de Tailandia, con aeropuerto internacional y estación de partida hacia Koh Tao y Koh Phangan. Turismo en fuerte crecimiento, precios aún competitivos frente a Phuket o Koh Samui. Tailandia ofrece un marco legal estable para compradores extranjeros mediante sociedad local.',
      },
    },
    {
      q: {
        it: 'Quanto costa un appartamento a Chumphon?',
        en: 'How much does an apartment in Chumphon cost?',
        es: '¿Cuánto cuesta un apartamento en Chumphon?',
      },
      a: {
        it: 'Grazie alla promo pre-launch, tutti i prezzi Chumphon sono scontati del 30% rispetto agli altri Green Paradise fino a ottobre 2026. La Standard Suite parte da €106.750 (invece di €152.500). Piano pagamenti rateale con caparra, anticipo e rate mensili fino alla consegna.',
        en: 'Thanks to the pre-launch promo, all Chumphon prices are discounted 30% versus other Green Paradise properties until October 2026. The Standard Suite starts at €106,750 (instead of €152,500). Instalment payment plan with deposit, down payment, and monthly instalments through delivery.',
        es: 'Gracias a la promo pre-launch, todos los precios de Chumphon tienen un descuento del 30% respecto a los demás Green Paradise hasta octubre 2026. La Standard Suite parte de €106.750 (en lugar de €152.500). Plan de pagos en cuotas con depósito, anticipo y cuotas mensuales hasta la entrega.',
      },
    },
    {
      q: {
        it: 'Quanti appartamenti disponibili?',
        en: 'How many apartments are available?',
        es: '¿Cuántos apartamentos hay disponibles?',
      },
      a: {
        it: 'Nella fase pre-launch sono disponibili appartamenti in tutte le tipologie (Standard, King, King Pool, Rooftop). La disponibilità aggiornata è visibile sul modulo di qualifica. Prenota una call per il listino completo aggiornato.',
        en: 'During the pre-launch phase apartments are available in every typology (Standard, King, King Pool, Rooftop). Current availability is shown on the qualification form. Book a call for the full up-to-date price list.',
        es: 'En la fase pre-launch hay apartamentos disponibles en todas las tipologías (Standard, King, King Pool, Rooftop). La disponibilidad actualizada se ve en el formulario de calificación. Reserva una llamada para obtener la lista de precios completa.',
      },
    },
    {
      q: {
        it: 'Come funziona la proprietà in Thailandia per stranieri?',
        en: 'How does property ownership in Thailand work for foreigners?',
        es: '¿Cómo funciona la propiedad en Tailandia para extranjeros?',
      },
      a: {
        it: 'Il modello legale standard in Thailandia per acquirenti stranieri prevede l\u2019acquisto tramite società locale con partecipazione qualificata (structure BOI o similari) oppure leasehold di lungo periodo (30 anni rinnovabili). Il team Legal HCF gestisce tutta la documentazione con notai e avvocati locali.',
        en: 'The standard legal framework in Thailand for foreign buyers requires purchase through a local company with qualified participation (BOI structure or similar) or long-term leasehold (30 years, renewable). The HCF Legal team handles all documentation with local notaries and lawyers.',
        es: 'El marco legal estándar en Tailandia para compradores extranjeros prevé la compra a través de sociedad local con participación calificada (estructura BOI o similar) o leasehold a largo plazo (30 años renovables). El equipo Legal HCF gestiona toda la documentación con notarios y abogados locales.',
      },
    },
    {
      q: {
        it: 'Quando apre Chumphon?',
        en: 'When will Chumphon open?',
        es: '¿Cuándo abre Chumphon?',
      },
      a: {
        it: 'Chumphon è in fase "coming soon". Il masterplan è definito, la fase di pre-launch è aperta con prezzi scontati -30% fino a ottobre 2026. Grand opening previsto entro il 2028-2029.',
        en: 'Chumphon is in a "coming soon" phase. The masterplan is set, the pre-launch is open with 30%-off prices until October 2026. Grand opening expected within 2028-2029.',
        es: 'Chumphon está en fase "coming soon". El masterplan está definido, la fase pre-launch está abierta con precios con -30% de descuento hasta octubre 2026. Grand opening previsto para 2028-2029.',
      },
    },
    {
      q: {
        it: 'Perché la promo -30% pre-launch?',
        en: 'Why the -30% pre-launch promo?',
        es: '¿Por qué la promo -30% pre-launch?',
      },
      a: {
        it: 'Il pre-launch premia chi entra prima delle campagne di marketing internazionali. Riconosciamo il rischio commerciale iniziale (progetto nuovo, brand ancora in costruzione localmente) con uno sconto di lancio. Dopo ottobre 2026 i prezzi si allineeranno agli altri Green Paradise.',
        en: 'The pre-launch rewards those who enter before the international marketing campaigns. We acknowledge the early-stage commercial risk (new project, brand still being built locally) with a launch discount. After October 2026 prices will align with the other Green Paradise properties.',
        es: 'El pre-launch premia a quienes entran antes de las campañas de marketing internacionales. Reconocemos el riesgo comercial inicial (proyecto nuevo, marca aún en construcción localmente) con un descuento de lanzamiento. A partir de octubre 2026 los precios se alinearán con los demás Green Paradise.',
      },
    },
    {
      q: {
        it: 'Posso visitare il sito a Chumphon?',
        en: 'Can I visit the Chumphon site?',
        es: '¿Puedo visitar el sitio en Chumphon?',
      },
      a: {
        it: 'Sì, organizziamo Verification Pass Thailandia con cadenza trimestrale. Aereo scontato per chi conclude qualifica, tour del sito, incontro con partner locali. Contattaci per la prossima finestra disponibile.',
        en: 'Yes, we organise Verification Passes to Thailand on a quarterly basis. Discounted flights for those who complete qualification, site tour, meeting with local partners. Contact us for the next available window.',
        es: 'Sí, organizamos Verification Pass Tailandia con cadencia trimestral. Vuelo descontado para quienes completan la calificación, tour del sitio, encuentro con socios locales. Contáctanos para la próxima ventana disponible.',
      },
    },
    {
      q: {
        it: 'Chumphon vs Lombok — quale scegliere?',
        en: 'Chumphon vs Lombok — which to choose?',
        es: 'Chumphon vs Lombok — ¿cuál elegir?',
      },
      a: {
        it: 'Sono entrambi ottimi progetti in Asia con modelli legali diversi (Thailandia = società/leasehold, Indonesia = Hak Pakai). Chumphon è nella fase pre-launch con sconto -30% aggressivo, Lombok è in fase più avanzata con costruzione già iniziata. La scelta dipende dal tuo profilo. Parla con un Senior Associate per capire quale fa per te.',
        en: 'Both are excellent Asian projects with different legal models (Thailand = company/leasehold, Indonesia = Hak Pakai). Chumphon is in an aggressive -30% pre-launch phase, Lombok is at a more advanced stage with construction already underway. The choice depends on your profile. Speak with a Senior Associate to figure out which one is right for you.',
        es: 'Ambos son excelentes proyectos en Asia con modelos legales diferentes (Tailandia = sociedad/leasehold, Indonesia = Hak Pakai). Chumphon está en fase pre-launch con un descuento -30% agresivo, Lombok está en fase más avanzada con la construcción ya iniciada. La elección depende de tu perfil. Habla con un Senior Associate para saber cuál es la adecuada para ti.',
      },
    },
  ],

  'la-mar-residence': [
    {
      q: {
        it: 'Cos\u2019è La Mar Residence?',
        en: 'What is La Mar Residence?',
        es: '¿Qué es La Mar Residence?',
      },
      a: {
        it: 'La Mar Residence è un condominio residenziale premium fronte mare situato a Golem (Albania). 21 bilocali distribuiti su 3 blocchi (A/B/C), finiture di alto livello, vista mare diretta. È un progetto Real Estate puro: compri l\u2019appartamento con atto notarile e piano pagamenti rateale.',
        en: 'La Mar Residence is a premium seafront residential condominium located in Golem (Albania). 21 two-room units across 3 blocks (A/B/C), high-end finishes, direct sea view. It\u2019s a pure Real Estate project: you purchase the apartment with a notarial deed and an instalment payment plan.',
        es: 'La Mar Residence es un condominio residencial premium frente al mar situado en Golem (Albania). 21 dos ambientes distribuidos en 3 bloques (A/B/C), acabados de alto nivel, vista al mar directa. Es un proyecto Real Estate puro: compras el apartamento con escritura notarial y plan de pagos en cuotas.',
      },
    },
    {
      q: {
        it: 'Quanto costa un appartamento a La Mar Residence?',
        en: 'How much does an apartment at La Mar Residence cost?',
        es: '¿Cuánto cuesta un apartamento en La Mar Residence?',
      },
      a: {
        it: 'I bilocali partono da €80.554 (53,8 mq). C\u2019è anche una tipologia più ampia da 60 mq. Piano pagamenti: caparra €5.000 + anticipo alla firma (55%) + rate 2-3 (30%) + saldo alla consegna 2027 (8%). Nessun mutuo bancario richiesto.',
        en: 'Two-room units start at €80,554 (53.8 sqm). There is also a larger 60 sqm typology. Payment plan: €5,000 deposit + 55% down payment on signature + 30% instalments 2-3 + 8% balance at 2027 delivery. No bank mortgage required.',
        es: 'Los dos ambientes parten de €80.554 (53,8 m²). También hay una tipología más amplia de 60 m². Plan de pagos: depósito €5.000 + anticipo del 55% a la firma + cuotas 2-3 (30%) + saldo del 8% en la entrega 2027. Sin necesidad de hipoteca bancaria.',
      },
    },
    {
      q: {
        it: 'La Mar Residence ha una promessa di rendimento?',
        en: 'Does La Mar Residence promise a yield?',
        es: '¿La Mar Residence promete rentabilidad?',
      },
      a: {
        it: 'C\u2019è una proiezione indicativa di ROI netto annuo del 5% dalla consegna (ipotesi conservativa da business plan). Non è una promessa di rendimento garantito: il risultato dipenderà dalle politiche di gestione locativa, dall\u2019occupancy stagionale e dal mercato turistico albanese.',
        en: 'There is an indicative 5% net annual ROI projection from delivery (conservative business-plan assumption). This is not a guaranteed return promise: the actual result depends on rental management, seasonal occupancy, and the Albanian tourism market.',
        es: 'Hay una proyección indicativa de ROI neto anual del 5% desde la entrega (hipótesis conservadora del business plan). No es una promesa de rentabilidad garantizada: el resultado dependerá de las políticas de gestión locativa, la ocupación estacional y el mercado turístico albanés.',
      },
    },
    {
      q: {
        it: 'Quando apre La Mar Residence?',
        en: 'When will La Mar Residence open?',
        es: '¿Cuándo abre La Mar Residence?',
      },
      a: {
        it: 'Consegna prevista nel 2027. Il cantiere è avviato e riceverai report periodici sull\u2019avanzamento.',
        en: 'Delivery scheduled for 2027. Construction has started and you will receive periodic progress reports.',
        es: 'Entrega prevista en 2027. La obra está en marcha y recibirás reportes periódicos del avance.',
      },
    },
    {
      q: {
        it: 'Come funziona il pagamento?',
        en: 'How does the payment work?',
        es: '¿Cómo funciona el pago?',
      },
      a: {
        it: 'Piano rateale in 5 tranche: caparra €5.000 · anticipo 55% mese 1 · rata 15% mese 2-3 · rata 15% mese 4-5 · saldo 8% alla consegna. Contratto notarile albanese, freehold diretto per stranieri. Il team Legal HCF gestisce tutta la documentazione.',
        en: 'Five-tranche instalment plan: €5,000 deposit · 55% down payment month 1 · 15% instalment months 2-3 · 15% instalment months 4-5 · 8% balance at delivery. Albanian notarial contract, direct freehold for foreigners. The HCF Legal team handles all documentation.',
        es: 'Plan en cuotas de 5 tramos: depósito €5.000 · anticipo 55% mes 1 · cuota 15% meses 2-3 · cuota 15% meses 4-5 · saldo 8% en la entrega. Contrato notarial albanés, freehold directo para extranjeros. El equipo Legal HCF gestiona toda la documentación.',
      },
    },
    {
      q: {
        it: 'Posso visitare il cantiere a Golem?',
        en: 'Can I visit the construction site in Golem?',
        es: '¿Puedo visitar la obra en Golem?',
      },
      a: {
        it: 'Sì, con un Verification Pass Albania. Contattaci per la prossima finestra disponibile: aereo scontato, tour del cantiere e della zona di Golem, incontro con il team locale.',
        en: 'Yes, with a Verification Pass to Albania. Contact us for the next available window: discounted flight, tour of the construction site and Golem area, meeting with the local team.',
        es: 'Sí, con un Verification Pass Albania. Contáctanos para la próxima ventana disponible: vuelo descontado, tour de la obra y de la zona de Golem, encuentro con el equipo local.',
      },
    },
    {
      q: {
        it: 'La Mar Residence o Narea? Come scegliere?',
        en: 'La Mar Residence or Narea? How to choose?',
        es: '¿La Mar Residence o Narea? ¿Cómo elegir?',
      },
      a: {
        it: 'Sono entrambi progetti Real Estate in Albania. La Mar Residence è più compatta (21 unità, Golem, consegna 2027, prezzi da €80k). Narea è un landmark residenziale più grande (161 unità su 14 piani, Querret, consegna 2029, prezzi da €134k). Parla con un Senior Associate per capire quale fa per te.',
        en: 'Both are Real Estate projects in Albania. La Mar Residence is more compact (21 units, Golem, 2027 delivery, prices from €80k). Narea is a larger residential landmark (161 units across 14 floors, Querret, 2029 delivery, prices from €134k). Talk to a Senior Associate to figure out which one fits you.',
        es: 'Ambos son proyectos Real Estate en Albania. La Mar Residence es más compacto (21 unidades, Golem, entrega 2027, precios desde €80k). Narea es un landmark residencial más grande (161 unidades en 14 plantas, Querret, entrega 2029, precios desde €134k). Habla con un Senior Associate para saber cuál te conviene.',
      },
    },
  ],

  narea: [
    {
      q: {
        it: 'Cos\u2019è Narea?',
        en: 'What is Narea?',
        es: '¿Qué es Narea?',
      },
      a: {
        it: 'Narea è un landmark residenziale in Albania (Querret) di 14 piani con 161 appartamenti (bilocali, trilocali, attici). Progetto Real Estate puro: compri l\u2019appartamento con atto notarile e piano pagamenti rateale.',
        en: 'Narea is a residential landmark in Albania (Querret) with 14 floors and 161 apartments (two-room, three-room, penthouses). A pure Real Estate project: you purchase the apartment with a notarial deed and instalment payment plan.',
        es: 'Narea es un landmark residencial en Albania (Querret) de 14 plantas con 161 apartamentos (dos ambientes, tres ambientes, áticos). Proyecto Real Estate puro: compras el apartamento con escritura notarial y plan de pagos en cuotas.',
      },
    },
    {
      q: {
        it: 'Quanto costa un appartamento Narea?',
        en: 'How much does a Narea apartment cost?',
        es: '¿Cuánto cuesta un apartamento en Narea?',
      },
      a: {
        it: 'A partire da €134.420. Diverse tipologie disponibili con tagli e piani differenti. Piano pagamenti: caparra €15.000 + anticipo alla firma (40%) + 5 rate mensili (45%) + saldo alla consegna 2029 (15%).',
        en: 'Starting from €134,420. Multiple typologies available with different sizes and floor plans. Payment plan: €15,000 deposit + 40% down payment on signature + 5 monthly instalments (45%) + 15% balance at 2029 delivery.',
        es: 'A partir de €134.420. Varias tipologías disponibles con distintas superficies y plantas. Plan de pagos: depósito €15.000 + anticipo del 40% a la firma + 5 cuotas mensuales (45%) + saldo del 15% en la entrega 2029.',
      },
    },
    {
      q: {
        it: 'Quanti appartamenti sono disponibili?',
        en: 'How many apartments are available?',
        es: '¿Cuántos apartamentos hay disponibles?',
      },
      a: {
        it: 'Su 161 unità totali, circa 53 sono ancora disponibili. La disponibilità aggiornata è visibile sulla pagina del progetto.',
        en: 'Out of 161 total units, about 53 are still available. Updated availability is visible on the project page.',
        es: 'De 161 unidades totales, alrededor de 53 aún están disponibles. La disponibilidad actualizada se ve en la página del proyecto.',
      },
    },
    {
      q: {
        it: 'Quando apre Narea?',
        en: 'When will Narea open?',
        es: '¿Cuándo abre Narea?',
      },
      a: {
        it: 'Consegna prevista 2029. Il progetto è in fase di sviluppo, con timeline di costruzione condivise trimestralmente.',
        en: 'Delivery scheduled for 2029. The project is in development, with construction timelines shared quarterly.',
        es: 'Entrega prevista para 2029. El proyecto está en fase de desarrollo, con cronogramas de construcción compartidos trimestralmente.',
      },
    },
    {
      q: {
        it: 'Come funziona la proprietà in Albania per stranieri?',
        en: 'How does property ownership in Albania work for foreigners?',
        es: '¿Cómo funciona la propiedad en Albania para extranjeros?',
      },
      a: {
        it: 'Freehold diretto: proprietà a tutti gli effetti, con atto notarile e registrazione al catasto albanese. Il team Legal HCF gestisce tutta la documentazione. Fiscalità immobiliare vantaggiosa, tassazione locativa contenuta.',
        en: 'Direct freehold: full legal ownership, with notarial deed and registration in the Albanian land registry. The HCF Legal team handles all documentation. Favourable property taxation and low rental tax rates.',
        es: 'Freehold directo: propiedad a todos los efectos, con escritura notarial e inscripción en el catastro albanés. El equipo Legal HCF gestiona toda la documentación. Fiscalidad inmobiliaria ventajosa, tributación locativa reducida.',
      },
    },
    {
      q: {
        it: 'Posso visitare Narea a Querret?',
        en: 'Can I visit Narea in Querret?',
        es: '¿Puedo visitar Narea en Querret?',
      },
      a: {
        it: 'Sì, con un Verification Pass Albania. Ti portiamo direttamente sul sito, incontri il team locale, verifichi lo stato lavori. Contattaci per la prossima finestra disponibile.',
        en: 'Yes, with a Verification Pass to Albania. We take you directly to the site, you meet the local team and verify progress. Contact us for the next available window.',
        es: 'Sí, con un Verification Pass Albania. Te llevamos directamente al sitio, conoces al equipo local y verificas el estado de los trabajos. Contáctanos para la próxima ventana disponible.',
      },
    },
    {
      q: {
        it: 'Posso rivendere l\u2019appartamento in futuro?',
        en: 'Can I resell the apartment in the future?',
        es: '¿Puedo revender el apartamento en el futuro?',
      },
      a: {
        it: 'Sì. Alla consegna sei proprietario freehold e puoi rivendere sul mercato secondario. Il marketplace interno HCF Estate (hcf.estate) facilita l\u2019incontro fra proprietari e nuovi acquirenti.',
        en: 'Yes. At delivery you are a freehold owner and can resell on the secondary market. The internal HCF Estate marketplace (hcf.estate) connects sellers with new buyers.',
        es: 'Sí. En la entrega eres propietario freehold y puedes revender en el mercado secundario. El marketplace interno HCF Estate (hcf.estate) facilita el encuentro entre propietarios y nuevos compradores.',
      },
    },
  ],
  // ★ 2026-08-28 — Palm Paradise (Querret, Albania · 198 unità · 4 blocchi · Q2 2027)
  'palm-paradise': [
    {
      q: {
        it: 'Cos\u2019è Palm Paradise?',
        en: 'What is Palm Paradise?',
        es: '¿Qué es Palm Paradise?',
      },
      a: {
        it: 'Palm Paradise è un quartiere residenziale a Querret (Albania) di 4 blocchi (A1, C1, C4, C5) con 198 appartamenti immersi in corti tropicali con piscine e viali di palme. Progetto Real Estate puro: compri l\u2019appartamento con atto notarile e piano pagamenti rateale.',
        en: 'Palm Paradise is a residential district in Querret (Albania) with 4 blocks (A1, C1, C4, C5) and 198 apartments set in tropical courtyards with pools and palm-lined walkways. A pure Real Estate project: you purchase the apartment with a notarial deed and instalment payment plan.',
        es: 'Palm Paradise es un barrio residencial en Querret (Albania) de 4 bloques (A1, C1, C4, C5) con 198 apartamentos en patios tropicales con piscinas y paseos de palmeras. Proyecto Real Estate puro: compras el apartamento con escritura notarial y plan de pagos en cuotas.',
      },
    },
    {
      q: {
        it: 'Quanto costa un appartamento Palm Paradise?',
        en: 'How much does a Palm Paradise apartment cost?',
        es: '¿Cuánto cuesta un apartamento en Palm Paradise?',
      },
      a: {
        it: 'A partire da €120.919 (da €1.755/mq). Piano pagamenti come da listino: caparra confirmatoria €5.000 + anticipo alla firma + rata intermedia + saldo alla consegna Q2 2027.',
        en: 'Starting from €120,919 (from €1,755/sqm). Payment plan as per the official price list: €5,000 confirmatory deposit + down payment on signature + intermediate instalment + balance at Q2 2027 delivery.',
        es: 'A partir de €120.919 (desde €1.755/m²). Plan de pagos según lista oficial: depósito confirmatorio de €5.000 + anticipo a la firma + cuota intermedia + saldo en la entrega Q2 2027.',
      },
    },
    {
      q: {
        it: 'Quanti appartamenti sono disponibili?',
        en: 'How many apartments are available?',
        es: '¿Cuántos apartamentos hay disponibles?',
      },
      a: {
        it: 'Su 198 unità totali, 35 sono ancora disponibili nei 4 blocchi (tagli da 68 a 91 mq). La disponibilità aggiornata è visibile sulla pagina del progetto.',
        en: 'Out of 198 total units, 35 are still available across the 4 blocks (from 68 to 91 sqm). Updated availability is visible on the project page.',
        es: 'De 198 unidades totales, 35 aún están disponibles en los 4 bloques (de 68 a 91 m²). La disponibilidad actualizada se ve en la página del proyecto.',
      },
    },
    {
      q: {
        it: 'Quando viene consegnato Palm Paradise?',
        en: 'When will Palm Paradise be delivered?',
        es: '¿Cuándo se entrega Palm Paradise?',
      },
      a: {
        it: 'Consegna prevista nel secondo trimestre 2027 (Q2 2027). Il progetto è in fase di sviluppo, con timeline di costruzione condivise trimestralmente.',
        en: 'Delivery scheduled for the second quarter of 2027 (Q2 2027). The project is in development, with construction timelines shared quarterly.',
        es: 'Entrega prevista para el segundo trimestre de 2027 (Q2 2027). El proyecto está en fase de desarrollo, con cronogramas compartidos trimestralmente.',
      },
    },
    {
      q: {
        it: 'Come funziona la proprietà in Albania per stranieri?',
        en: 'How does property ownership in Albania work for foreigners?',
        es: '¿Cómo funciona la propiedad en Albania para extranjeros?',
      },
      a: {
        it: 'Freehold diretto: proprietà a tutti gli effetti, con atto notarile e registrazione al catasto albanese. Il team Legal HCF gestisce tutta la documentazione. Fiscalità immobiliare vantaggiosa, tassazione locativa contenuta.',
        en: 'Direct freehold: full legal ownership, with notarial deed and registration in the Albanian land registry. The HCF Legal team handles all documentation. Favourable property taxation and low rental tax rates.',
        es: 'Freehold directo: propiedad a todos los efectos, con escritura notarial e inscripción en el catastro albanés. El equipo Legal HCF gestiona toda la documentación. Fiscalidad inmobiliaria ventajosa, tributación locativa reducida.',
      },
    },
    {
      q: {
        it: 'Posso visitare Palm Paradise a Querret?',
        en: 'Can I visit Palm Paradise in Querret?',
        es: '¿Puedo visitar Palm Paradise en Querret?',
      },
      a: {
        it: 'Sì, con un Verification Pass Albania. Ti portiamo direttamente sul sito, incontri il team locale, verifichi lo stato lavori. Contattaci per la prossima finestra disponibile.',
        en: 'Yes, with a Verification Pass to Albania. We take you directly to the site, you meet the local team and verify progress. Contact us for the next available window.',
        es: 'Sí, con un Verification Pass Albania. Te llevamos directamente al sitio, conoces al equipo local y verificas el estado de los trabajos. Contáctanos para la próxima ventana disponible.',
      },
    },
  ],
  // ★ 2026-08-29 — Vista Mare (Querret, Albania · 189 unità · Aprile 2027)
  'vista-mare': [
    {
      q: {
        it: 'Cos\u2019è Vista Mare?',
        en: 'What is Vista Mare?',
        es: '¿Qué es Vista Mare?',
      },
      a: {
        it: 'Vista Mare è un residence a Querret (Albania) di 189 appartamenti su 7 piani, a 2 minuti dalla spiaggia dell\u2019Adriatico con vista mare. Progetto Real Estate puro: compri l\u2019appartamento con atto notarile e piano pagamenti rateale.',
        en: 'Vista Mare is a residence in Querret (Albania) with 189 apartments on 7 floors, 2 minutes from the Adriatic beach with sea view. A pure Real Estate project: you purchase the apartment with a notarial deed and instalment payment plan.',
        es: 'Vista Mare es un residence en Querret (Albania) de 189 apartamentos en 7 plantas, a 2 minutos de la playa del Adriático con vista al mar. Proyecto Real Estate puro: compras el apartamento con escritura notarial y plan de pagos en cuotas.',
      },
    },
    {
      q: {
        it: 'Quanto costa un appartamento Vista Mare?',
        en: 'How much does a Vista Mare apartment cost?',
        es: '¿Cuánto cuesta un apartamento en Vista Mare?',
      },
      a: {
        it: 'A partire da €56.800 (da €1.600/mq) — il ticket Real Estate più accessibile del portfolio. Piano pagamenti: caparra confirmatoria 3% + anticipo 30% + 5 rate mensili + saldo alla consegna Aprile 2027.',
        en: 'Starting from €56,800 (from €1,600/sqm) — the most accessible Real Estate ticket in the portfolio. Payment plan: 3% confirmatory deposit + 30% down payment + 5 monthly instalments + balance at April 2027 delivery.',
        es: 'A partir de €56.800 (desde €1.600/m²) — el ticket Real Estate más accesible del portafolio. Plan de pagos: depósito confirmatorio 3% + anticipo 30% + 5 cuotas mensuales + saldo en la entrega de abril 2027.',
      },
    },
    {
      q: {
        it: 'Quanti appartamenti sono disponibili?',
        en: 'How many apartments are available?',
        es: '¿Cuántos apartamentos hay disponibles?',
      },
      a: {
        it: 'Su 189 unità totali, solo 6 sono ancora disponibili (tagli da 35,5 a 67 mq). La disponibilità aggiornata è visibile sulla pagina del progetto.',
        en: 'Out of 189 total units, only 6 are still available (from 35.5 to 67 sqm). Updated availability is visible on the project page.',
        es: 'De 189 unidades totales, solo 6 siguen disponibles (de 35,5 a 67 m²). La disponibilidad actualizada se ve en la página del proyecto.',
      },
    },
    {
      q: {
        it: 'Quando viene consegnato Vista Mare?',
        en: 'When will Vista Mare be delivered?',
        es: '¿Cuándo se entrega Vista Mare?',
      },
      a: {
        it: 'Consegna prevista ad Aprile 2027. Il progetto è in costruzione, con timeline condivise trimestralmente.',
        en: 'Delivery scheduled for April 2027. The project is under construction, with timelines shared quarterly.',
        es: 'Entrega prevista para abril de 2027. El proyecto está en construcción, con cronogramas compartidos trimestralmente.',
      },
    },
    {
      q: {
        it: 'Come funziona la proprietà in Albania per stranieri?',
        en: 'How does property ownership in Albania work for foreigners?',
        es: '¿Cómo funciona la propiedad en Albania para extranjeros?',
      },
      a: {
        it: 'Freehold diretto: proprietà a tutti gli effetti, con atto notarile e registrazione al catasto albanese. Il team Legal HCF gestisce tutta la documentazione. Fiscalità immobiliare vantaggiosa, tassazione locativa contenuta.',
        en: 'Direct freehold: full legal ownership, with notarial deed and registration in the Albanian land registry. The HCF Legal team handles all documentation. Favourable property taxation and low rental tax rates.',
        es: 'Freehold directo: propiedad a todos los efectos, con escritura notarial e inscripción en el catastro albanés. El equipo Legal HCF gestiona toda la documentación. Fiscalidad inmobiliaria ventajosa, tributación locativa reducida.',
      },
    },
    {
      q: {
        it: 'Posso visitare Vista Mare a Querret?',
        en: 'Can I visit Vista Mare in Querret?',
        es: '¿Puedo visitar Vista Mare en Querret?',
      },
      a: {
        it: 'Sì, con un Verification Pass Albania. Ti portiamo direttamente sul sito, incontri il team locale, verifichi lo stato lavori. Contattaci per la prossima finestra disponibile.',
        en: 'Yes, with a Verification Pass to Albania. We take you directly to the site, you meet the local team and verify progress. Contact us for the next available window.',
        es: 'Sí, con un Verification Pass Albania. Te llevamos directamente al sitio, conoces al equipo local y verificas el estado de los trabajos. Contáctanos para la próxima ventana disponible.',
      },
    },
  ],
  // ★ 2026-08-29 — Green Paradise Lombok (Bumbang Bay · 32 ville · Ottobre 2027)
  'green-paradise-lombok': [
    {
      q: {
        it: 'Cos\u2019è Green Paradise Lombok?',
        en: 'What is Green Paradise Lombok?',
        es: '¿Qué es Green Paradise Lombok?',
      },
      a: {
        it: 'Green Paradise Lombok è il resort eco-luxury ufficiale HCF a Bumbang Bay, sud di Lombok (Indonesia): 32 ville private fronte oceano con spiaggia privata. Acquisti la villa con piano pagamenti rateale e la gestione hospitality è curata dal resort.',
        en: 'Green Paradise Lombok is the official HCF eco-luxury resort in Bumbang Bay, southern Lombok (Indonesia): 32 private oceanfront villas with a private beach. You purchase the villa with an instalment plan and hospitality management is handled by the resort.',
        es: 'Green Paradise Lombok es el resort eco-lujo oficial de HCF en Bumbang Bay, sur de Lombok (Indonesia): 32 villas privadas frente al océano con playa privada. Compras la villa con plan de pagos y la gestión hospitality la cura el resort.',
      },
    },
    {
      q: {
        it: 'Che differenza c\u2019è tra Villa Green e Villa Paradise?',
        en: 'What is the difference between Villa Green and Villa Paradise?',
        es: '¿Qué diferencia hay entre Villa Green y Villa Paradise?',
      },
      a: {
        it: 'Villa Green: 160 mq interni + 130-175 mq esterni con giardino e piscina privata, da €257.000 (14 disponibili). Villa Paradise: 80 mq interni + 40 mq esterni con terrazza, €116.000 (15 disponibili).',
        en: 'Villa Green: 160 sqm interior + 130-175 sqm exterior with garden and private pool, from €257,000 (14 available). Villa Paradise: 80 sqm interior + 40 sqm exterior with terrace, €116,000 (15 available).',
        es: 'Villa Green: 160 m² interiores + 130-175 m² exteriores con jardín y piscina privada, desde €257.000 (14 disponibles). Villa Paradise: 80 m² interiores + 40 m² exteriores con terraza, €116.000 (15 disponibles).',
      },
    },
    {
      q: {
        it: 'Come funziona il piano pagamenti?',
        en: 'How does the payment plan work?',
        es: '¿Cómo funciona el plan de pagos?',
      },
      a: {
        it: 'Come da listino ufficiale: caparra confirmatoria + anticipo alla firma + 5 rate + saldo. Ogni villa ha il suo piano dettagliato nella proposta ufficiale.',
        en: 'As per the official price list: confirmatory deposit + down payment on signature + 5 instalments + balance. Each villa has its detailed plan in the official proposal.',
        es: 'Según la lista oficial: depósito confirmatorio + anticipo a la firma + 5 cuotas + saldo. Cada villa tiene su plan detallado en la propuesta oficial.',
      },
    },
    {
      q: {
        it: 'Quando apre il resort?',
        en: 'When does the resort open?',
        es: '¿Cuándo abre el resort?',
      },
      a: {
        it: 'Apertura operativa prevista a Ottobre 2027. Dalla consegna la villa entra nel circuito hospitality del resort con ROI netto stimato del 15% annuo.',
        en: 'Operational opening scheduled for October 2027. From delivery, the villa joins the resort hospitality circuit with an estimated net ROI of 15% per year.',
        es: 'Apertura operativa prevista para octubre de 2027. Desde la entrega, la villa entra en el circuito hospitality del resort con un ROI neto estimado del 15% anual.',
      },
    },
    {
      q: {
        it: 'Come funziona la proprietà in Indonesia per stranieri?',
        en: 'How does property ownership in Indonesia work for foreigners?',
        es: '¿Cómo funciona la propiedad en Indonesia para extranjeros?',
      },
      a: {
        it: 'Tramite struttura legale dedicata (Hak Pakai / leasehold di lungo periodo) predisposta dal team Legal HCF con atti e documentazione ufficiale. Tutti i documenti sono condivisi prima della firma.',
        en: 'Through a dedicated legal structure (Hak Pakai / long-term leasehold) prepared by the HCF Legal team with official deeds and documentation. All documents are shared before signing.',
        es: 'Mediante una estructura legal dedicada (Hak Pakai / leasehold a largo plazo) preparada por el equipo Legal HCF con actas y documentación oficial. Todos los documentos se comparten antes de la firma.',
      },
    },
    {
      q: {
        it: 'Posso visitare il resort a Bumbang Bay?',
        en: 'Can I visit the resort in Bumbang Bay?',
        es: '¿Puedo visitar el resort en Bumbang Bay?',
      },
      a: {
        it: 'Sì, con un Verification Pass Indonesia: ti portiamo sul sito di Bumbang Bay, incontri il team locale e verifichi il progetto di persona. Contattaci per la prossima finestra disponibile.',
        en: 'Yes, with a Verification Pass to Indonesia: we take you to the Bumbang Bay site, you meet the local team and verify the project in person. Contact us for the next available window.',
        es: 'Sí, con un Verification Pass Indonesia: te llevamos al sitio de Bumbang Bay, conoces al equipo local y verificas el proyecto en persona. Contáctanos para la próxima ventana disponible.',
      },
    },
  ],
};
